import dayjs from 'dayjs';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChartHeader from 'components/charts/ChartHeader';
import ProgressCard from 'components/charts/ProgressCard';
import BarChartV2 from 'components/charts/chartJS/BarChart';
import DateFilter from 'components/field/DateFilter';
import Card from 'components/global/Card';
import { calcultateCustomWidgetDateFilter } from 'components/global/ManageWidgets/widgetsUtils';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { REPORT } from 'services/consts';

import { colorCombinations } from 'utils/colors';

const SLAPerformance = () => {
	return (
		<WidgetPageContainer>
			<WidgetWrapper widgetId={'analysis.sla.delay'}>
				<SLAOverview />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'preparationSLAPerformance'}>
				<PreparationSLAPerformance parseData={parseData} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'pickupSLAPerformance'}>
				<PickupSLAPerformance parseData={parseData} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'deliverySLAPerformance'}>
				<DeliverySLAPerformance parseData={parseData} />
			</WidgetWrapper>
		</WidgetPageContainer>
	);
};

export const SLAOverview = ({ widgetName, onApplyFilters, defaultFilters }) => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(6, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const skipNextApply = useRef(false);

	useEffect(() => {
		if (defaultFilters) {
			if (defaultFilters.from && defaultFilters.to) {
				setFilter({ start: defaultFilters.from, end: defaultFilters.to });
			}
			skipNextApply.current = true;
		}
	}, [defaultFilters]);

	useEffect(() => {
		if (onApplyFilters) {
			if (skipNextApply.current) {
				skipNextApply.current = false;
				return;
			}
			onApplyFilters(body);
		}
	}, [body]);

	const handleDefaultValue = useCallback(() => {
		return calcultateCustomWidgetDateFilter(defaultFilters);
	}, [defaultFilters]);

	const progressCardConfig = [
		{
			timeLabel: 'averagePreparationTime',
			time: 'avgPreparation',
			percentageLabel: 'preparationRate',
			percentage: 'preparationPercentage',
			color: '#3949ab'
		},
		{
			timeLabel: 'averagePickupTime',
			time: 'avgPickup',
			percentageLabel: 'pickupRate',
			percentage: 'pickupPercentage',
			color: '#2196f3'
		},
		{
			timeLabel: 'averageDeliveryTime',
			time: 'avgDelivery',
			percentageLabel: 'deliveryRate',
			percentage: 'deliveryPercentage',
			color: '#43a047'
		}
	];

	return (
		<>
			<ChartHeader
				title={widgetName || 'analysis.sla.delay'}
				filter={
					<DateFilter
						defaultDay={handleDefaultValue()?.defaultDay || 'last7Days'}
						setValue={setFilter}
						defaultFilter={handleDefaultValue()?.defaultFilter || null}
					/>
				}
				showDivider={false}
			/>
			<ProgressCard
				body={body}
				fetchURL={REPORT.SLA.GET_SLA_PERFORMANCE}
				loadingCardNumber={3}
				dataConfig={progressCardConfig}
			/>
		</>
	);
};

const parseData = (data, t) => {
	const labels = data.map((item) => item.date);
	const datasets = [
		{
			label: t('outsideSLA'),
			data: data.map((item) => item.outsideSLA),
			backgroundColor: colorCombinations[0].color,
			borderColor: colorCombinations[0].color
		},
		{
			label: t('insideSLA'),
			data: data.map((item) => item.insideSLA),
			backgroundColor: colorCombinations[1].color,
			borderColor: colorCombinations[1].color
		}
	];
	return { labels, datasets };
};

export const PreparationSLAPerformance = ({ widgetName, onApplyFilters, defaultFilters }) => {
	const { t } = useTranslation();
	const [filter, setFilter] = useState({
		start: dayjs().subtract(6, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const skipNextApply = useRef(false);

	useEffect(() => {
		if (defaultFilters) {
			if (defaultFilters.from && defaultFilters.to) {
				setFilter({ start: defaultFilters.from, end: defaultFilters.to });
			}
			skipNextApply.current = true;
		}
	}, [defaultFilters]);

	useEffect(() => {
		if (onApplyFilters) {
			if (skipNextApply.current) {
				skipNextApply.current = false;
				return;
			}
			onApplyFilters(body);
		}
	}, [body]);

	const handleDefaultValue = useCallback(() => {
		return calcultateCustomWidgetDateFilter(defaultFilters);
	}, [defaultFilters]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SLA.GET_PREPARATION_SLA,
		body,
		parseData: (data) => parseData(data, t)
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={widgetName || 'preparationSLAPerformance'}
				filter={
					<DateFilter
						defaultDay={handleDefaultValue()?.defaultDay || 'last7Days'}
						setValue={setFilter}
						defaultFilter={handleDefaultValue()?.defaultFilter || null}
					/>
				}
			/>
			<BarChartV2 xTitle="date" yTitle="numberOfShipments" data={data} loading={loading} />
		</Card>
	);
};

export const PickupSLAPerformance = ({ widgetName }) => {
	const { t } = useTranslation();

	const [filter, setFilter] = useState({
		start: dayjs().subtract(6, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SLA.GET_PICKUP_SLA,
		body,
		parseData: (data) => parseData(data, t)
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={widgetName || 'pickupSLAPerformance'}
				filter={<DateFilter defaultDay={'last7Days'} setValue={setFilter} />}
			/>
			<BarChartV2 xTitle="date" yTitle="numberOfShipments" data={data} loading={loading} />
		</Card>
	);
};

export const DeliverySLAPerformance = ({ widgetName, onApplyFilters, defaultFilters }) => {
	const { t } = useTranslation();

	const [filter, setFilter] = useState({
		start: dayjs().subtract(6, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const skipNextApply = useRef(false);

	useEffect(() => {
		if (defaultFilters) {
			if (defaultFilters.from && defaultFilters.to) {
				setFilter({ start: defaultFilters.from, end: defaultFilters.to });
			}
			skipNextApply.current = true;
		}
	}, [defaultFilters]);

	useEffect(() => {
		if (onApplyFilters) {
			if (skipNextApply.current) {
				skipNextApply.current = false;
				return;
			}
			onApplyFilters(body);
		}
	}, [body]);

	const handleDefaultValue = useCallback(() => {
		return calcultateCustomWidgetDateFilter(defaultFilters);
	}, [defaultFilters]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SLA.GET_DELIVERY_SLA,
		body,
		parseData: (data) => parseData(data, t)
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={widgetName || 'deliverySLAPerformance'}
				filter={
					<DateFilter
						defaultDay={handleDefaultValue()?.defaultDay || 'last7Days'}
						setValue={setFilter}
						defaultFilter={handleDefaultValue()?.defaultFilter || null}
					/>
				}
			/>
			<BarChartV2 xTitle="date" yTitle="numberOfShipments" data={data} loading={loading} />
		</Card>
	);
};

export default SLAPerformance;
